import { Navigate, useLocation } from "react-router-dom";
import { useMyAccounts } from "@/hooks/use-accounts";
import { QueryError } from "@/components/query-error";

export function WelcomeGuard({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const { data, isLoading, isError, refetch } = useMyAccounts();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        Loading...
      </div>
    );
  }

  // A load error must NOT fall through to rendering the app: doing so treats a
  // transient query error as "no onboarding needed", so a new user skips
  // onboarding and a later successful refetch can yank an existing user to
  // /welcome mid-session. Block on an explicit retry surface instead.
  if (isError) {
    // eslint-disable-next-line no-console
    console.error("[WelcomeGuard] failed to load accounts");
    return (
      <div className="min-h-screen flex items-center justify-center">
        <QueryError
          message="Couldn't load your account. Please try again."
          onRetry={() => void refetch()}
        />
      </div>
    );
  }

  const accounts = data?.accounts ?? [];
  const active = accounts.find((a) => a.id === data?.active_account_id);
  // Fail-open when no active account matches (active === undefined →
  // needsOnboarding false): a user with no/mismatched membership proceeds into
  // the app rather than being trapped on /welcome. Deliberate.
  const needsOnboarding =
    active != null && active.onboarding_completed_at === null;

  if (needsOnboarding && location.pathname !== "/welcome") {
    return <Navigate to="/welcome" replace />;
  }

  return <>{children}</>;
}
